/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import OpenInNew from '@mui/icons-material/OpenInNew';
import isNil from 'lodash/isNil';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Terminal, { InstallationType } from '../../shared/entities/terminal';
import { Ticket } from '../../shared/entities/ticket';
import { fetchTickets } from '../../store/tickets/actions';
import { TicketsState } from '../../store/tickets/reducer';
import { dateToUTCDate, dateToUTCTime } from '../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../utils/StringUtils';
import { getTerminalDisplayName } from '../../utils/TerminalUtilities';
import Scrollbars from '../common/layout/Scrollbars';
import StyledProps from '../common/theme/AppThemeInterface';
import appTheme from '../common/theme/appTheme.json';

const StyledTableCell = {
  minWidth: '25px',
  lineHeight: '18px',
  letterSpacing: '0.5px',
  color: `${appTheme.primaryTextColor}80`,
  backgroundColor: appTheme.primaryBackgroundColor,
};

const StyledTableSortLabel = styled(TableSortLabel)``;
const StyledArrowDownwardRoundedIcon = styled(ArrowDownwardRoundedIcon)`
  color: ${({ theme }: StyledProps) => theme.buttonSelectedColor} !important;
  font-size: 18px !important;
`;
const StyledTableSortLabelInactive = styled(TableSortLabel)`
  &.Mui-active {
    color: ${({ theme }: StyledProps) => theme.primaryTextColor}80;
  }
  &:hover {
    color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
    opacity: 0.5;
  }
`;
const StyledSortRoundedIconInactive = styled(SortRoundedIcon)`
  color: ${({ theme }: StyledProps) => theme.primaryTextColor}80 !important;
  font-size: 18px !important;
`;

export const CaseLink = styled.a`
  color: ${({ theme }: StyledProps) => theme.externalLinkColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-size: ${({ theme }: StyledProps) => theme.defaultFontSize};
  text-decoration: none;
  display: flex;
  align-items: center;
  text-align: right;

  &:hover {
    text-decoration: underline;
  }

  svg {
    height: 16px;
  }
`;

export interface TicketsTableProps {
  tickets: Readonly<TicketsState>;
  salesforceBaseURL: string;
  terminals: { [key: string]: Terminal };
}
const TicketsTable = (props: TicketsTableProps) => {
  const { tickets, salesforceBaseURL, terminals } = props;
  const dispatch = useDispatch();
  return (
    <Scrollbars autoHeight autoHeightMin={0} autoHeightMax="calc(100vh - 200px)">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...StyledTableCell }}>TERMINAL</TableCell>
            <TableCell sx={{ ...StyledTableCell }}>SUBJECT</TableCell>
            <TableCell sx={{ ...StyledTableCell }}>
              {!!(
                tickets &&
                tickets.options &&
                tickets.options.sort &&
                tickets.options.sort.key === ('createdDate' as keyof Ticket)
              ) ? (
                <StyledTableSortLabel
                  active={
                    !!(
                      tickets &&
                      tickets.options &&
                      tickets.options.sort &&
                      tickets.options.sort.key === ('createdDate' as keyof Ticket)
                    )
                  }
                  onClick={() => {
                    if (tickets.options && tickets.options.sort) {
                      dispatch(
                        fetchTickets({
                          ...tickets.options,
                          sort: { ...tickets.options.sort, direction: !tickets.options.sort.direction },
                        }),
                      );
                    }
                  }}
                  direction={tickets.options && tickets.options.sort && tickets.options.sort.direction ? 'asc' : 'desc'}
                  IconComponent={StyledArrowDownwardRoundedIcon}
                >
                  CREATED
                </StyledTableSortLabel>
              ) : (
                <StyledTableSortLabelInactive
                  active={
                    !!(
                      tickets &&
                      tickets.options &&
                      tickets.options.sort &&
                      tickets.options.sort.key !== ('createdDate' as keyof Ticket)
                    )
                  }
                  direction={'desc'}
                  onClick={() => {
                    if (tickets.options && tickets.options.sort) {
                      dispatch(
                        fetchTickets({
                          ...tickets.options,
                          sort: {
                            ...tickets.options.sort,
                            key: 'createdDate' as keyof Ticket,
                            sortKey: 'CREATED_DATE',
                          },
                        }),
                      );
                    }
                  }}
                  IconComponent={StyledSortRoundedIconInactive}
                >
                  CREATED
                </StyledTableSortLabelInactive>
              )}
            </TableCell>
            <TableCell sx={{ ...StyledTableCell }}>
              <Box minWidth="140px">
                {tickets &&
                tickets.options &&
                tickets.options.sort &&
                tickets.options.sort.key === ('updatedDate' as keyof Ticket) ? (
                  <StyledTableSortLabel
                    active={
                      !!(
                        tickets &&
                        tickets.options &&
                        tickets.options.sort &&
                        tickets.options.sort.key === ('updatedDate' as keyof Ticket)
                      )
                    }
                    onClick={() => {
                      if (tickets.options && tickets.options.sort) {
                        dispatch(
                          fetchTickets({
                            ...tickets.options,
                            sort: { ...tickets.options.sort, direction: !tickets.options.sort.direction },
                          }),
                        );
                      }
                    }}
                    direction={
                      tickets.options && tickets.options.sort && tickets.options.sort.direction ? 'asc' : 'desc'
                    }
                    IconComponent={StyledArrowDownwardRoundedIcon}
                  >
                    LAST UPDATED
                  </StyledTableSortLabel>
                ) : (
                  <StyledTableSortLabelInactive
                    active={
                      !!(
                        tickets &&
                        tickets.options &&
                        tickets.options.sort &&
                        tickets.options.sort.key !== ('updatedDate' as keyof Ticket)
                      )
                    }
                    direction={'desc'}
                    onClick={() => {
                      if (tickets.options && tickets.options.sort) {
                        dispatch(
                          fetchTickets({
                            ...tickets.options,
                            sort: {
                              ...tickets.options.sort,
                              key: 'updatedDate' as keyof Ticket,
                              sortKey: 'UPDATED_DATE',
                            },
                          }),
                        );
                      }
                    }}
                    IconComponent={StyledSortRoundedIconInactive}
                  >
                    LAST UPDATED
                  </StyledTableSortLabelInactive>
                )}
              </Box>
            </TableCell>
            <TableCell sx={{ ...StyledTableCell }}>PRIORITY</TableCell>
            <TableCell sx={{ ...StyledTableCell }}>CASE</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.projection
            .map((ticketKey: string) => tickets.map[ticketKey])
            .slice()
            .sort((a: Ticket, b: Ticket) => {
              if (tickets.options && tickets.options.sort && tickets.options.sort.direction) {
                return a.updatedDate < b.updatedDate ? 1 : -1;
              } else {
                return a.updatedDate > b.updatedDate ? 1 : -1;
              }
            })
            .map((ticket: Ticket) => (
              <TableRow key={ticket.id}>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>
                  {getTerminalNameUsingTicketTailNumber(terminals, ticket.tailNumber)}
                </TableCell>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>{ticket.subject}</TableCell>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>
                  {dateToUTCDate(ticket.createdDate)} {dateToUTCTime(ticket.createdDate, 'HH:mm')}
                </TableCell>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>
                  {dateToUTCDate(ticket.updatedDate)} {dateToUTCTime(ticket.updatedDate, 'HH:mm')}
                </TableCell>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>{ticket.priority}</TableCell>
                <TableCell sx={{ ...StyledTableCell, color: appTheme.primaryTextColor }}>
                  {!isNil(ticket.caseNumber) ? (
                    <CaseLink target="_blank" href={`${salesforceBaseURL}/${ticket.id}`}>
                      {ticket.caseNumber}
                      <OpenInNew />
                    </CaseLink>
                  ) : (
                    EMPTY_FIELD_STRING_PLACEHOLDER
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Scrollbars>
  );
};
export default TicketsTable;

/**
 * This method searches the terminal map for a terminal that
 * matches the tail number from the ticket.
 *
 * NOTE: For dual terminals, it is expected that the ticket's
 * tail number is set as the terminal display name. This will
 * be used to associate the ticket to the dual terminal in the
 * system.
 *
 * @param terminals - terminal map
 * @param ticketTailNumber - tail number from the ticket
 * @returns terminal display name of matching terminal, '--' if no match
 */
export function getTerminalNameUsingTicketTailNumber(terminals: { [key: number]: Terminal }, ticketTailNumber: string) {
  return getTerminalDisplayName(
    Object.keys(terminals)
      .map((key) => terminals[key])
      .find((terminal: Terminal) =>
        terminal.installationType === InstallationType.DUAL_TERMINAL
          ? terminal.terminalDisplayName === ticketTailNumber
          : terminal.aircraftTailNumber === ticketTailNumber,
      ) || ({} as Terminal),
  );
}
